// src/components/infografia/properties/ImageProperties.jsx
import React, { useRef, useState } from 'react';
import { FaSearch, FaImages } from 'react-icons/fa';
import infografiaService from '../../../helpers/infografia/infografiaService';
import Swal from 'sweetalert2';
import ImageLibraryModal from '../modals/ImageLibraryModal';

const ImageProperties = ({ element, onUpdate, marcaId, infografiaId, onOpenImageSearch }) => {
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [showImageLibrary, setShowImageLibrary] = useState(false);
    const props = element.propiedades || {};

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validar tipo de archivo - solo imágenes permitidas
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            Swal.fire({
                title: 'Tipo de archivo no válido',
                text: 'Solo se permiten archivos de imagen (JPEG, PNG, WebP, GIF)',
                icon: 'error'
            });
            return;
        }

        // Validar tamaño (max 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
            Swal.fire({
                title: 'Archivo demasiado grande',
                text: `La imagen es demasiado grande. Máximo 10MB (tamaño actual: ${(file.size / 1024 / 1024).toFixed(2)}MB)`,
                icon: 'error'
            });
            return;
        }

        // Validar dimensiones básicas (opcional, se puede hacer en backend)
        // Por ahora solo validamos tipo y tamaño

        try {
            setUploading(true);

            // Subir imagen al backend
            // Si tenemos infografiaId, usar el endpoint específico de infografía
            // Si no, usar el método genérico (requiere que el backend lo soporte)
            let response;
            if (infografiaId) {
                // Usar el endpoint específico de infografía
                response = await infografiaService.uploadImagen(infografiaId, file, element.id);
            } else {
                // Método alternativo si no tenemos infografiaId todavía
                const formData = new FormData();
                formData.append('imagen', file);
                formData.append('marcaId', marcaId);
                response = await infografiaService.uploadImage(marcaId, formData);
            }

            const imageData = response?.imagen || response;

            if (!imageData || (!imageData.cdnUrl && !imageData.url)) {
                throw new Error('No se recibió la URL de la imagen del servidor');
            }

            // Actualizar propiedades con la URL del CDN
            onUpdate({
                propiedades: {
                    ...props,
                    cdnUrl: imageData.cdnUrl || imageData.url,
                    url: imageData.url || imageData.cdnUrl,
                    key: imageData.key,
                    originalName: file.name,
                    opacidad: props.opacidad || 1
                }
            });

            Swal.fire({
                title: '¡Imagen subida!',
                text: 'La imagen se ha subido correctamente',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Error subiendo imagen:', error);
            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo subir la imagen. Por favor, intenta de nuevo.',
                icon: 'error'
            });
        } finally {
            setUploading(false);
            // Limpiar el input para permitir subir la misma imagen de nuevo
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    return (
        <div className="space-y-4">
            {/* Vista previa */}
            {(props.cdnUrl || props.url) ? (
                <div className="mb-4">
                    <img
                        src={props.cdnUrl || props.url}
                        alt="Preview"
                        className="w-full h-48 object-contain border border-gray-300 rounded-md bg-gray-50"
                        onError={(e) => {
                            console.error('Error cargando imagen:', props.cdnUrl || props.url);
                            e.target.style.display = 'none';
                        }}
                    />
                </div>
            ) : (
                <div className="mb-4 p-8 border-2 border-dashed border-gray-300 rounded-md bg-gray-50 text-center">
                    <p className="text-gray-500 text-sm">No hay imagen seleccionada</p>
                    <p className="text-gray-400 text-xs mt-1">Sube una imagen para verla aquí</p>
                </div>
            )}

            {/* Subir imagen */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Imagen
                </label>
                <div className="space-y-2">
                    {/* Botón para ver biblioteca de imágenes */}
                    <button
                        onClick={() => setShowImageLibrary(true)}
                        className="w-full px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 flex items-center justify-center gap-2"
                    >
                        <FaImages />
                        Ver Imágenes Guardadas
                    </button>

                    {/* TODO: Habilitar cuando se configuren las API keys de Unsplash y Pexels */}
                    {/* {onOpenImageSearch && (
                        <button
                            onClick={onOpenImageSearch}
                            className="w-full px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 flex items-center justify-center gap-2"
                        >
                            <FaSearch />
                            Buscar en Internet
                        </button>
                    )} */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                    />
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="w-full px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {uploading ? 'Subiendo...' : (props.cdnUrl || props.url ? 'Cambiar Imagen' : 'Subir Nueva Imagen')}
                    </button>
                </div>
            </div>

            {/* Modal de biblioteca de imágenes */}
            <ImageLibraryModal
                isOpen={showImageLibrary}
                onClose={() => setShowImageLibrary(false)}
                onSelectImage={(imageData) => {
                    onUpdate({
                        propiedades: {
                            ...props,
                            cdnUrl: imageData.cdnUrl,
                            url: imageData.url,
                            key: imageData.key,
                            originalName: imageData.originalName,
                            opacidad: props.opacidad || 1
                        }
                    });
                    Swal.fire({
                        title: '¡Imagen seleccionada!',
                        text: 'La imagen se ha aplicado correctamente',
                        icon: 'success',
                        timer: 2000,
                        showConfirmButton: false
                    });
                }}
                marcaId={marcaId}
                onUploadNew={() => {
                    setShowImageLibrary(false);
                    setTimeout(() => fileInputRef.current?.click(), 100);
                }}
            />

            {/* Opacidad */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Opacidad: {Math.round((props.opacidad || 1) * 100)}%
                </label>
                <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={props.opacidad || 1}
                    onChange={(e) => onUpdate({
                        propiedades: {
                            ...props,
                            opacidad: parseFloat(e.target.value)
                        }
                    })}
                    className="w-full"
                />
            </div>
        </div>
    );
};

export default ImageProperties;

